import { motion, useInView } from "motion/react";
import classNames from "classnames/bind";
import { Container, Image, RhLink } from "@components";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { GetAllInducteesQuery } from "@/__generated__/graphql";
import { sortInductees } from "@/core/utils";

import styles from "../InducteesByClass.module.scss";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { AnimVals } from "@/utils/animUtils";
import { useClientMount } from "@/providers/ClientMountProvider";
import { useViewSize } from "@/providers/ViewSizeContext";
const cx = classNames.bind(styles);

type Inductee = GetAllInducteesQuery["allInductees"][number];
type InducteesByClassProps = {
  inductees: Inductee[];
  listEl?: HTMLDivElement | null;
  year: string;
};

const InducteeGridItem = ({ inductee }: { inductee: Inductee }) => {
  const {
    uri,
    title,
    pageData: { featuredImage },
    inducteeData: { inducteeClass, category },
  } = inductee;
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loadImage, setLoadImage] = useState(false);
  const elRef = useRef<HTMLAnchorElement>(null);
  const inView = useInView(elRef, { once: AnimVals.once });
  useEffect(() => {
    if (inView && featuredImage?.node?.mediaItemUrl && !loadImage) {
      setLoadImage(true);
    }
  }, [inView, featuredImage?.node?.mediaItemUrl]);
  return (
    <RhLink
      href={uri}
      key={title}
      className={cx("inductee-item")}
      ref={elRef}
      prefetch={false}
    >
      <div className={cx(["image-wrapper", { "image-loaded": imageLoaded }])}>
        <span className={cx(["placeholder"])} />
        {loadImage && (
          <Image
            image={featuredImage}
            alt={featuredImage.node.altText || title}
            onLoad={() => setImageLoaded(true)}
            className={cx("image")}
          />
        )}
      </div>
      <div className={cx("copy")}>
        <p className={cx("name", "text-m")}>{title}</p>
        <p className={cx("pill", "class", "MD-TagSmall")}>
          {category[0] || "Unknown Category"}
        </p>
      </div>
    </RhLink>
  );
};

const InducteesByClassGrid = forwardRef<HTMLDivElement, InducteesByClassProps>(
  ({ inductees, listEl, year }, ref) => {
    const gridEl = useRef<HTMLDivElement>(null);
    const { mounted } = useClientMount();
    const { isReady } = useViewSize();
    
    useGSAP(() => {
      if (!mounted || !isReady) return;
      gsap.fromTo(
        gridEl.current.children,
        {
          y: 100,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          ease: "power3.out",
          duration: 0.6,
          stagger: 0.03,
        }
      );
    }, [mounted, isReady, gridEl]);

    const sortedInductees = useMemo(
      () => sortInductees(inductees),
      [inductees]
    );

    useEffect(() => {
      const handleGridKeyDown = (event: KeyboardEvent) => {
        const { key } = event;
        const activeElm = document.activeElement as HTMLAnchorElement;
        const links = gridEl.current.querySelectorAll("a");
        const currentIndex = Array.from(links).findIndex(
          (el) => el.href === activeElm.href
        );
        const cardsPerRow = 6;
        let nextIndex = -1;

        if (event.key === "Tab") {
          if (event.shiftKey) {
            const elm = document.querySelector(
              `div[data-year="${year}"] button`
            ) as HTMLDivElement;

            if (elm) {
              event.preventDefault();
              elm.focus();
            }
          } else {
            event.preventDefault();
            const footer = document.querySelector("footer");
            const nextFocusable = footer?.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            ) as HTMLElement;
            if (nextFocusable) {
              nextFocusable?.focus();
              footer.scrollIntoView();
            }
          }
        }

        switch (key) {
          case "ArrowDown":
            nextIndex = currentIndex + cardsPerRow;
            break;
          case "ArrowUp":
            nextIndex = currentIndex - cardsPerRow;
            break;
          case "ArrowLeft":
            nextIndex = currentIndex - 1;
            break;
          case "ArrowRight":
            nextIndex = currentIndex + 1;
            break;
        }

        if (currentIndex !== -1 && nextIndex !== -1) {
          event.preventDefault();
          links[nextIndex]?.focus();
        }
      };

      gridEl.current?.addEventListener("keydown", handleGridKeyDown);

      return () => {
        gridEl.current?.removeEventListener("keydown", handleGridKeyDown);
      };
    }, []);

    return (
      <div className={cx("inductees-by-class-grid", "grid-el")} ref={ref}>
        <Container className={cx("container")}>
          <div className={cx("inductees-grid")} ref={gridEl}>
            {sortedInductees.map((inductee) => (
              <InducteeGridItem key={inductee.uri} inductee={inductee} />
            ))}
          </div>
        </Container>
      </div>
    );
  }
);

export default motion.create(InducteesByClassGrid);
